import React from 'react';
import { useSelector } from 'react-redux';
import { Loading, StringHelpers, SystemText } from '@salesperformanceint/e4shared';
import Filters from '../_filters';
import Chart from './chart';
import GroupPicker from 'components/admin/forms/groupPicker';
import MetricPicker from 'components/admin/forms/metricPicker';
import WidgetBase, {
    groupSet,
    groupTooLong,
    metricSet,
    metricFormatFromId,
    widgetLoad,
    widgetLoadRecommendations,
} from '../_base';
import CompetencyRecommendations from '../components/competencyRecommendations';

export const MetricOverTime = ({ defaultProps, id, item, setItem, onDelete }) => {
    const showFilters = useSelector(state => state.ui.filterWidget);
    const user = useSelector(state => state.user.user);
    const [values, setValues] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [notConfigured, setNotConfigured] = React.useState(false);
    const [initialised, setInitialised] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [recLoading, setRecLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [recommendationData, setRecommendationData] = React.useState({});

    const metrics = useSelector(state => state.dashboard.metrics);

    React.useEffect(() => {
        if (item) {
            const parsed = JSON.parse(item.data);
            setValues({ ...defaults, ...parsed });
            setInitialised(true);
        }
    }, [item]);

    React.useEffect(() => {
        initialised && load();
    }, [initialised]);

    const load = () => {
        widgetLoad(item.id, user.guid, validator, setLoading, setNotConfigured, setData, () => {
            widgetLoadRecommendations(item.id, user.guid, setRecLoading, setRecommendationData);
        });
    };

    const updateProperty = (value, prop) => {
        const temp = { ...values };
        temp[prop] = value;
        setValues(temp);
    };

    const st = prop => SystemText(`Dashboard/Widgets/MetricOverTime/${prop}`);

    const height = defaultProps.height(item);

    const defaults = { show_For: true, period: 'M' };

    const validator = suppressErrors => {
        const form = values || JSON.parse(item.data);
        const errors = {};
        let validForm = true;

        if (!form.name) {
            errors.name = SystemText('Dashboard/Widgets/General/Edit/Fields/Name/Error');
            validForm = false;
        }

        if (!groupSet(form.group1)) {
            errors.group1 = st('Fields/Group/Error/Mandatory');
            validForm = false;
        }

        if (groupTooLong(form.group1)) {
            errors.group1 = st('Fields/Group/Error/TooLong');
            validForm = false;
        }

        if (!metricSet(form.metric1)) {
            errors.metric1 = st('Fields/Metric/Error');
            validForm = false;
        }

        if (form.metric1?.subMetricId && (!form.metric1?.subMetricValues || form.metric1?.subMetricValues.length === 0)) {
            errors.metric1 = st('Fields/SubMetric/Error');
            validForm = false;
        }

        if (!suppressErrors) setErrors(errors);
        return validForm;
    };

    const chart = (
        <div className="chart-container" id={`chart-${item.id}`} style={{ height: `${height}px` }}>
            <div className="chart" style={{ height: `${height}px` }}>
                <Chart data={[data]} item={item} id={`chart-${item.id}`} />
            </div>
        </div>
    );

    const recommendations = () => {
        if (recLoading) return <Loading />;

        const data = JSON.parse(item.data);
        const metricFormat = data?.metric1 ? metricFormatFromId(data.metric1, metrics) : 'Q';

        return recommendationData?.comps.length ? (
            <CompetencyRecommendations
                text={SystemText(
                    'Dashboard/Widgets/MetricOverTime/Recommends/Strongest',
                    recommendationData?.best,
                    StringHelpers.formatMetric(metricFormat, recommendationData?.score, user.currencySymbol)
                )}
                comps={recommendationData?.comps}
            />
        ) : (
            <span>{SystemText('Dashboard/Widgets/Recommends/NoData')}</span>
        );
    };

    const filters = (
        <Filters
            validator={validator}
            widget={item}
            values={values}
            errors={errors}
            onChange={(value, prop) => updateProperty(value, prop)}
            refresh={x => {
                setItem(x);
                load();
            }}
            show={showFilters === id}
            onDelete={onDelete}
        >
            <GroupPicker
                mandatory
                allowMultiple={false}
                value={values.group1}
                error={errors.group1}
                onChange={value => updateProperty(value, 'group1')}
                help={st('Fields/Group/Help')}
                showUserOption={defaultProps.company}
                controlId={id}
            />
            <MetricPicker
                label={st('Fields/Metric/Label')}
                mandatory
                value={values.metric1}
                error={errors.metric1}
                onChange={value => updateProperty(value, 'metric1')}
                showAverage
            />
        </Filters>
    );
    return (
        <WidgetBase
            filters={filters}
            chart={chart}
            height={height}
            item={item}
            recommendations={recommendations()}
            id={id}
            help={null}
            hideConfig={defaultProps.company && !user.administrator}
            extra={null}
            loading={loading}
            notConfigured={notConfigured}
        />
    );
};

export default MetricOverTime;
